import cx from 'classnames'
import React from 'react'
import Icon from 'components/Icon'
import { observer } from 'mobx-react'
import {
  CellProps,
  CheckCellContent,
  CheckCellProps,
  ColumnDensity,
  DataTestProps,
  DateDisplay,
  FormatDate,
  ImageCellContent,
  ImageCellProps,
  TextCellContent,
} from './TableCell'
import Checkbox from 'components/Checkbox'
import styles from './Table.sass'

interface TableHostProps {
  children?: React.ReactNode
}

/**
 * Table Host, adding border and stretching + filling layout so that
 * the table is stretched across the available horizontal space and fills
 * the available vertical space. The parent must be configured as a flex
 * container for those effects.
 */
export function TableHost({ children }: TableHostProps) {
  return <div className={styles.host}>{children}</div>
}

interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  striped?: boolean
  stickyHeader?: boolean
}

export const Table = React.forwardRef(
  (
    { className, striped = false, stickyHeader = false, ...rest }: TableProps,
    ref: React.Ref<HTMLTableElement>
  ) => {
    return (
      <table
        ref={ref}
        className={cx(
          styles.base,
          striped && styles.striped,
          stickyHeader && styles.stickyHeader,
          className
        )}
        {...rest}
      ></table>
    )
  }
)

interface TableHead extends React.HTMLAttributes<HTMLTableSectionElement> {}

export const TableHead = React.forwardRef(
  (
    { className, children, ...rest }: TableHead,
    ref: React.Ref<HTMLTableSectionElement>
  ) => {
    return (
      <thead ref={ref} className={cx(styles.base, className)} {...rest}>
        <tr>{children}</tr>
      </thead>
    )
  }
)

export type TableSortOrder = 'asc' | 'desc' | 'none'

interface TableHeader extends React.HTMLAttributes<HTMLTableHeaderCellElement> {
  /**
   * If a TableHeader `canSort` then it will display a sort indicator and
   * make the header clickable
   */
  canSort?: boolean

  /**
   * sets the alignment for the th element, it could be left, right or center
   */
  align?: 'left' | 'right' | 'center'

  /**
   * If a sort order is specified, a sort order indicator will be displayed
   * Thiw will replace the default hasOrder indicator
   */
  sortOrder?: TableSortOrder

  /**
   * A callback that will be called with the next sortOrder when
   * this table header is clicked
   */
  onSort?: (sortOrder: TableSortOrder) => void
}
export const TableHeader = React.forwardRef(
  (
    {
      className,
      children,
      canSort = false,
      sortOrder = 'asc',
      onSort = () => null,
      alignment,
      ...rest
    }: TableHeader & AlignmentProps,
    ref: React.Ref<HTMLTableCellElement>
  ) => {
    const El = canSort ? 'a' : 'span'
    return (
      <th
        ref={ref}
        className={cx(className, alignment && styles[alignment])}
        {...rest}
      >
        <El
          role={canSort ? 'button' : ''}
          onClick={() => {
            if (!canSort) {
              return
            }
            switch (sortOrder) {
              case 'none':
                onSort('asc')
                break
              case 'asc':
                onSort('desc')
                break
              default:
                onSort('none')
            }
          }}
          className={cx(canSort && styles.headerWithSort)}
        >
          {children}
          {canSort && (
            <span
              className={cx(
                styles.sortIcon,
                sortOrder !== 'none' && styles.sortIconActive
              )}
            >
              {sortOrder === 'none' ? (
                <Icon size={20} name="dropdown" />
              ) : (
                <Icon
                  size={12}
                  name="sorting"
                  rotate={sortOrder === 'asc' ? -180 : 0}
                />
              )}
            </span>
          )}
        </El>
      </th>
    )
  }
)

interface TableBody extends React.HTMLAttributes<HTMLTableSectionElement> {}

export const TableBody = React.forwardRef(
  (
    { className, ...rest }: TableBody,
    ref: React.Ref<HTMLTableSectionElement>
  ) => {
    return (
      <tbody
        ref={ref}
        className={cx(styles.tableBody, className)}
        {...rest}
      ></tbody>
    )
  }
)

interface TableRow extends React.HTMLAttributes<HTMLTableRowElement> {
  odd?: boolean
}

export const TableRow = React.forwardRef(
  (
    { className, children, odd, ...rest }: TableRow,
    ref: React.Ref<HTMLTableRowElement>
  ) => {
    return (
      <tr
        ref={ref}
        className={cx(styles.row, odd && styles.odd, className)}
        {...rest}
      >
        {children}
      </tr>
    )
  }
)

interface TableCell extends React.HTMLAttributes<HTMLTableCellElement> {
  align?: 'left' | 'right' | 'center'
  width?: number
  dataTest?: string
}

export const TableCell = React.forwardRef(
  (
    {
      className,
      children,
      width,
      align = 'left',
      style,
      dataTest,
      ...rest
    }: TableCell,
    ref: React.Ref<HTMLTableCellElement>
  ) => {
    return (
      <td
        data-test={dataTest}
        ref={ref}
        align={align}
        className={cx(styles.row, className)}
        style={{
          ...(width ? { width } : undefined),
          ...style,
        }}
        {...rest}
      >
        {children}
      </td>
    )
  }
)

interface AlignmentProps {
  alignment?: 'left' | 'center' | 'right'
}

export const TextCell = observer(function TextCell({
  children,
  alignment,
  dataTest,
}: CellProps & AlignmentProps & DataTestProps) {
  return (
    <TableCell className={cx(styles.textCell, alignment && styles[alignment])}>
      <TextCellContent dataTest={dataTest}>{children}</TextCellContent>
    </TableCell>
  )
})

interface DateCellProps {
  date: Date | null
  density?: ColumnDensity
  format?: FormatDate
}

export const DateCell = observer(function DateCell({
  date,
  density,
  format,
  dataTest,
}: DateCellProps & DataTestProps) {
  return (
    <TextCell dataTest={dataTest}>
      {date && (
        <DateDisplay
          date={date}
          format={format}
          density={density || ColumnDensity.Default}
        />
      )}
    </TextCell>
  )
})

export const ImageCell = observer(function ImageCell({
  title,
  image,
  imageFallback,
  size,
  minWidth,
  noPadding,
}: ImageCellProps) {
  return (
    <TableCell>
      <ImageCellContent
        title={title}
        image={image}
        size={size}
        imageFallback={imageFallback}
        minWidth={minWidth}
        noPadding={noPadding}
      />
    </TableCell>
  )
})

export const CheckCell = observer(function CheckCell({
  checked,
  color,
}: CheckCellProps) {
  return (
    <TableCell>
      <CheckCellContent checked={checked} color={color} />
    </TableCell>
  )
})

interface CheckboxCellProps {
  checked?: boolean
  onChange?(checked: boolean): unknown
}

export const CheckboxCell = observer(function CheckboxCell({
  checked,
  onChange,
  dataTest,
}: CheckboxCellProps & DataTestProps) {
  return (
    <TableCell>
      <Checkbox checked={checked} onChange={onChange} data-test={dataTest} />
    </TableCell>
  )
})

export { default as TableEmptyStateContainer } from './TableEmptyStateContainer'
export { default as TableScrollingContainer } from './TableScrollingContainer'
